import { API_BASE_URL } from '../config/api';
import { SYP_PER_USD } from '../config/store';

/** Arabic language with Western digits 0–9 (avoids ٠–٩ from ar-SA default). */
export const AR_LOCALE_LATN = 'ar-SA-u-nu-latn';

const arNumber = (options = {}) =>
  new Intl.NumberFormat(AR_LOCALE_LATN, { numberingSystem: 'latn', ...options });

/**
 * Resolve product image URL. Returns full URL for relative paths, or null if no image / placeholder.
 */
export const getProductImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (/placeholder/i.test(u)) return null;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/api/') || u.startsWith('api/')) {
    return API_BASE_URL + (u.startsWith('/') ? u : '/' + u);
  }

  const mediaPath = u.replace(/^\/+/, '').replace(/^media\//i, '');
  const base = API_BASE_URL || '';
  return `${base}/media/${mediaPath}`;
};

/** Product/catalog amounts are stored in USD. */
export const formatCurrency = (amount, locale = 'en') => {
  const n = Number(amount || 0);
  if (locale === 'ar') {
    return arNumber({
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

export const sypToUsd = (sypAmount) => Number(sypAmount || 0) / SYP_PER_USD;

const formatSypOnly = (syp, locale) => {
  const n = Math.round(Number(syp || 0));
  if (locale === 'ar') {
    return arNumber({
      style: 'currency',
      currency: 'SYP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }
  return `${n.toLocaleString('en-US')} ل.س`;
};

const formatUsdForOrder = (usd, locale) => {
  const n = Number(usd || 0);
  if (locale === 'ar') {
    const ar = arNumber({
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
    return `${ar} $`;
  }
  const en = n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${en} $`;
};

/**
 * Order amounts are stored in SYP — show USD equivalent first, then SYP.
 * Example: 37.04 $ (555,600 ل.س)
 */
export const formatOrderDualCurrency = (sypAmount, locale = 'en') => {
  const syp = Math.round(Number(sypAmount || 0));
  const usd = sypToUsd(syp);
  return `${formatUsdForOrder(usd, locale)} (${formatSypOnly(syp, locale)})`;
};

/** @deprecated Use formatOrderDualCurrency */
export const formatOrderCurrency = formatOrderDualCurrency;

/** Display customer phone from DB (9 digits) as +963… */
export const formatSyrianPhone = (phone) => {
  if (!phone) return '';
  let p = String(phone).replace(/\D/g, '');
  if (p.startsWith('963')) return `+${p}`;
  if (p.startsWith('964')) return `+${p}`;
  if (p.length === 9) return `+963${p}`;
  return String(phone);
};

export const formatDate = (date, locale = 'en') => {
  if (!date) return '';
  const d = new Date(date);
  if (locale === 'ar') {
    return d.toLocaleDateString(AR_LOCALE_LATN, { year: 'numeric', month: 'long', day: 'numeric', numberingSystem: 'latn' });
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const formatDateTime = (date, locale = 'en') => {
  if (!date) return '';
  const d = new Date(date);
  if (locale === 'ar') {
    return d.toLocaleString(AR_LOCALE_LATN, { numberingSystem: 'latn' });
  }
  return d.toLocaleString('en-US');
};

export const formatTimeAgo = (timestamp, locale = 'en') => {
  const timeSpan = new Date() - new Date(timestamp);
  const minutes = Math.floor(timeSpan / 60000);
  const hours = Math.floor(timeSpan / 3600000);
  const days = Math.floor(timeSpan / 86400000);

  if (locale === 'ar') {
    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${days} يوم`;
  }
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};
